import { useEffect, useRef } from 'react';
import { useLenis } from 'lenis/react';
import { LAST_CHAPTER } from './constants';

const DURATION = 1.25; // seconds per chapter step
const GESTURE_GAP = 220; // ms without wheel input that ends one gesture
const SWIPE = 40; // px a touch must travel to count as a swipe
const ease = t => (t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2);

const editable = el => el?.closest?.('input, textarea, select, [contenteditable="true"]');

/*
 * One scroll, one chapter: inside the opening, a wheel gesture, trackpad flick, swipe or
 * arrow/page key glides the page to the next (or previous) chapter and lands exactly on
 * it, so every chapter's copy is always shown whole. The rest of the gesture (trackpad
 * inertia, extra wheel notches) is swallowed until it stops. Past the last chapter, or
 * above the first, input passes through and the page scrolls on as usual.
 */
export const useChapterSteps = (containerRef, progress, enabled) => {
  const lenis = useLenis();
  const state = useRef({ busy: false, stepped: false, lastWheel: 0, lastDelta: 0, timer: 0, touchY: null, touchStep: null });

  useEffect(() => {
    if (!lenis || !enabled) return undefined;
    const s = state.current;
    const span = () => {
      const el = containerRef.current;
      return { top: el.getBoundingClientRect().top + window.scrollY, length: el.offsetHeight - window.innerHeight };
    };
    const inside = () => {
      const { top, length } = span();
      return window.scrollY >= top - 2 && window.scrollY <= top + length + 2;
    };
    const targetFor = dir => {
      const r = progress.get();
      const k = dir > 0 ? Math.floor(r + 0.05) + 1 : Math.ceil(r - 0.05) - 1;
      return k >= 0 && k <= LAST_CHAPTER ? k : null;
    };
    const finish = () => {
      clearTimeout(s.timer);
      s.busy = false;
    };
    const go = k => {
      const { top, length } = span();
      s.busy = true;
      lenis.scrollTo(top + (k / LAST_CHAPTER) * length, { duration: DURATION, easing: ease, lock: true, force: true, onComplete: finish });
      clearTimeout(s.timer);
      s.timer = setTimeout(finish, DURATION * 1000 + 150);
    };
    const swallow = e => {
      e.preventDefault();
      e.stopImmediatePropagation();
    };

    // Runs before Lenis (capture on window), so a swallowed event never reaches it
    const onWheel = e => {
      if (e.ctrlKey || !e.deltaY || !inside()) return;
      const now = performance.now();
      const size = Math.abs(e.deltaY);
      // A new gesture: a pause, or a fresh push stronger than the fading inertia
      const fresh = now - s.lastWheel > GESTURE_GAP || (!s.busy && size >= 10 && size > s.lastDelta * 1.5);
      s.lastWheel = now;
      s.lastDelta = size;
      if (s.busy || (!fresh && s.stepped)) return swallow(e);
      const k = targetFor(Math.sign(e.deltaY));
      s.stepped = k !== null;
      if (k === null) return undefined;
      swallow(e);
      go(k);
      return undefined;
    };

    const onTouchStart = e => {
      s.touchY = e.touches.length === 1 && inside() ? e.touches[0].clientY : null;
      s.touchStep = null;
    };
    const onTouchMove = e => {
      if (s.touchY === null) return;
      const dy = s.touchY - e.touches[0].clientY;
      // Decide on the first move: once native scrolling starts it can't be taken back
      if (s.touchStep === null) {
        if (!dy) return;
        s.touchStep = s.busy || targetFor(Math.sign(dy)) !== null;
      }
      if (s.touchStep) e.preventDefault();
    };
    const onTouchEnd = e => {
      if (s.touchY === null || !s.touchStep) return;
      const dy = s.touchY - e.changedTouches[0].clientY;
      s.touchY = null;
      if (s.busy || Math.abs(dy) < SWIPE) return;
      const k = targetFor(Math.sign(dy));
      if (k !== null) go(k);
    };

    const onKey = e => {
      if (e.defaultPrevented || e.altKey || e.ctrlKey || e.metaKey || editable(e.target) || !inside()) return;
      if (e.key === ' ' && e.target.closest?.('button, a')) return;
      const dir = e.key === 'ArrowDown' || e.key === 'PageDown' || (e.key === ' ' && !e.shiftKey) ? 1 : e.key === 'ArrowUp' || e.key === 'PageUp' || (e.key === ' ' && e.shiftKey) ? -1 : 0;
      if (!dir) return;
      if (s.busy) {
        e.preventDefault();
        return;
      }
      const k = targetFor(dir);
      if (k === null) return;
      e.preventDefault();
      go(k);
    };

    const capture = { capture: true, passive: false };
    window.addEventListener('wheel', onWheel, capture);
    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchmove', onTouchMove, { passive: false });
    window.addEventListener('touchend', onTouchEnd, { passive: true });
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('wheel', onWheel, capture);
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onTouchEnd);
      window.removeEventListener('keydown', onKey);
      finish();
    };
  }, [lenis, enabled, containerRef, progress]);
};
